import React from "react"
import { INNER_MAX_WIDTH } from "../../constants"
import { Flex } from "../Flex"
import { Icon } from "../Icon"
import { ScrollPanelStyled, Inner, NavButton } from "./styled"
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect"

export const ScrollPanelContext = React.createContext({
	innerWidth: 0,
	slideWidth: 0,
	hasOverflow: false,
	innerRef: null as React.MutableRefObject<HTMLDivElement>,
	scrollPanelRef: null as React.MutableRefObject<HTMLDivElement>,
	scrollAtEnd: false,
	scrollAtStart: true,
	isTouch: false,
	scrollToSlide: (direction: "prev" | "next"): void => { },
})

export interface ScrollPanelProps {
	slideRef: React.MutableRefObject<HTMLDivElement>
	children?: React.ReactNode
}

export interface ScrollPanelContentProps {
	itemCount: number
	itemWidth: number
	children?: React.ReactNode
}

export interface ScrollPanelMembers {
	Content: React.FC<ScrollPanelContentProps>
	Navigation: React.FC<ScrollPanelNavigationProps>
}

const ScrollPanel: React.FC<ScrollPanelProps> & ScrollPanelMembers = (
	props
) => {
	// measurements
	const [innerWidth, setInnerWidth] = React.useState(0)
	const [slideWidth, setSlideWidth] = React.useState(0)
	// refs
	const innerRef = React.useRef<HTMLDivElement>(null)
	const scrollPanelRef = React.useRef<HTMLDivElement>(null)
	// bools
	const [hasOverflow, setHasOverflow] = React.useState(false)
	const [scrollAtEnd, setScrollAtEnd] = React.useState(false)
	const [scrollAtStart, setScrollAtStart] = React.useState(true)
	const [isTouch, setIsTouch] = React.useState(false)

	// take measurements
	useIsomorphicLayoutEffect(() => {
		if (innerRef.current) {
			const resizeHandler = () => {
				setInnerWidth(() => innerRef.current.scrollWidth)
				setSlideWidth(() => props.slideRef.current.scrollWidth)
			}
			resizeHandler()
			window.addEventListener("resize", resizeHandler)
			return () => window.removeEventListener("resize", resizeHandler)
		}
	}, [props.slideRef, innerRef])

	// check overflow
	React.useEffect(() => {
		const resizeHandler = () =>
			setHasOverflow(
				() =>
					scrollPanelRef.current.scrollWidth >
					scrollPanelRef.current.offsetWidth
			)
		resizeHandler()
		window.addEventListener("resize", resizeHandler)
		return () => window.removeEventListener("resize", resizeHandler)
	}, [scrollPanelRef])

	// check if scroll is at start or end
	React.useEffect(() => {
		const ref = scrollPanelRef.current
		const scrollListener = (e: Event) => {
			setScrollAtEnd(() => ref.offsetWidth + ref.scrollLeft === ref.scrollWidth)
			setScrollAtStart(() => scrollPanelRef.current.scrollLeft === 0)
		}
		if (ref) {
			ref.addEventListener("scroll", scrollListener, { passive: true })
		}
		return () => ref.removeEventListener("scroll", scrollListener)
	}, [scrollPanelRef])

	// check if scroll is at start or end
	React.useEffect(() => {
		const ref = scrollPanelRef.current
		const touchStartListener = (e: Event) => {
			setIsTouch(() => true)
			ref.removeEventListener("scroll", touchStartListener)
		}
		if (ref) {
			ref.addEventListener("touchstart", touchStartListener, { passive: true })
		}
		return () => ref.removeEventListener("scroll", touchStartListener)
	}, [scrollPanelRef])

	const scrollToSlide = (direction: "prev" | "next") => {
		setIsTouch(() => false)

		const scrollTarget =
			direction === "next"
				? Math.ceil((scrollPanelRef.current.scrollLeft + 1) / slideWidth) *
				slideWidth
				: Math.floor((scrollPanelRef.current.scrollLeft - 1) / slideWidth) *
				slideWidth

		if (scrollPanelRef.current) {
			scrollPanelRef.current.scroll({
				left: scrollTarget,
				behavior: "smooth",
			})
		}
	}

	return (
		<ScrollPanelContext.Provider
			value={{
				innerWidth,
				slideWidth,
				hasOverflow,
				innerRef,
				scrollPanelRef,
				scrollAtEnd,
				scrollAtStart,
				isTouch,
				scrollToSlide,
			}}
		>
			{props.children}
		</ScrollPanelContext.Provider>
	)
}

export const Content: React.FC<ScrollPanelContentProps> = ({
	itemCount,
	itemWidth,
	children,
}) => {
	const { scrollPanelRef, isTouch, innerRef } =
		React.useContext(ScrollPanelContext)
	// Base padding adjustment for keeping slider within grid
	// itemWidth * 2 - page gutter/2
	const [paddingAdjustment, setPaddingAdjustment] = React.useState<number>(
		itemWidth * 2 + 1.5
	)

	React.useEffect(() => {
		if (itemCount === 3) {
			setPaddingAdjustment(itemWidth + itemWidth / 2 + 1.5)
		} else if (itemCount === 2) {
			setPaddingAdjustment(itemWidth + 1.5)
		}
	}, [])

	return (
		<Flex flexDirection="column" alignItems="flex-end" w={1}>
			<ScrollPanelStyled ref={scrollPanelRef} scrollAuto={isTouch}>
				<Inner ref={innerRef} paddingAdjustment={paddingAdjustment}>
					{children}
				</Inner>
			</ScrollPanelStyled>
		</Flex>
	)
}

export interface ScrollPanelNavigationData {
	prevButtonLabel?: string
	nextButtonLabel?: string
}

export interface ScrollPanelNavigationProps {
	locales?: ScrollPanelNavigationData
}

export const Navigation = function (props) {
	const { scrollAtEnd, scrollAtStart, hasOverflow, scrollToSlide } =
		React.useContext(ScrollPanelContext)

	return (
		<>
			{hasOverflow && (
				<Flex
					w={1}
					maxw={INNER_MAX_WIDTH}
					justifyContent="center"
					mb={{ _: 3, m: 1 }}
					mt={1}
				>
					<Flex ml={2} mr={{ _: 0, m: 2 }}>
						<NavButton
							aria-label={props.locales?.prevButtonLabel || undefined}
							disabled={scrollAtStart}
							onClick={() => scrollToSlide("prev")}
							mr={2}
						>
							<Icon size={3} icon="arrow-left" color="white" />
						</NavButton>
						<NavButton
							aria-label={props.locales?.nextButtonLabel || undefined}
							disabled={scrollAtEnd}
							onClick={() => scrollToSlide("next")}
						>
							<Icon size={3} icon="arrow-right" color="white" />
						</NavButton>
					</Flex>
				</Flex>
			)}
		</>
	)
} as React.FC<ScrollPanelNavigationProps>

ScrollPanel.Content = Content
ScrollPanel.Navigation = Navigation

export { ScrollPanel }
